import React, { useState } from "react"
import { graphql, useStaticQuery } from "gatsby"
import {
  CoursesYaml,
  SemestersYaml,
  StposYaml,
  FilteredCoursesQuery,
} from "../../types/graphql-types"
import { CourseKind, KindList } from "./kinds"
import { Creditpoints } from "./creditpoints"
import { Appointments } from "./appointments"
import { ModuleHeading } from "./modules"
import Container from "./container"

import Foldable from "./foldable"
import { PartsList, PartItem, PartNote } from "./parts"
import StpoSelector from "./stpos"
import {
  semestersOrder,
  SemesterHeading,
  isSameOrAfterSemester,
} from "./semester"
import { categories, CategoryHeading } from "./categories"
import { Enrolment } from "./enrolment"

export const coursesQuery = graphql`
  fragment Courses on CoursesYaml {
    id
    enrolment {
      ...Enrolment
    }
    appointments {
      ...Appointments
    }
    kind {
      ...ModulesKinds
    }
    module {
      ...Modules
    }
    parts {
      ...CoursesParts
    }
    semester {
      ...Semesters
    }
    part {
      ...ModulesParts
    }
  }
`

export interface Course {
  course: CoursesYaml
}

export const CourseDetails: React.FC<Course> = ({ course }) => {
  return (
    <div class="text-umr-grey-600 mt-4">
      <div class="mb-6">
        {course.kind && !course.part ? (
          <CourseKind course={course} />
        ) : course.module.kinds ? (
          <KindList kinds={course.module.kinds} />
        ) : (
          <>
            <PartsList
              parts={course.module.parts}
              coursePart={course.part}
              courseKind={course.kind}
            />
          </>
        )}
        <Creditpoints creditpoints={course.module.creditpoints} />
      </div>

      {course.enrolment ? (
        <Enrolment enrolment={course.enrolment} className="mb-6" />
      ) : (
        ""
      )}

      <div>
        {course.parts ? (
          <ul class="list-disc ml-6 pn-6 mt-4">
            {course.parts.map(part => (
              <PartItem
                key={part.part.id}
                part={part.part}
                className="text-lg font-semibold"
                liClass="mb-4 last-child-mb-0"
                kindClassName=""
              >
                <PartNote part={part} />
                <Appointments
                  appointments={part.appointments}
                  className="mt-2"
                />
              </PartItem>
            ))}
          </ul>
        ) : course.kind ? (
          <CourseKind course={course} className="text-lg font-semibold">
            {course.part ? (
              <PartNote part={course.part} className="mt-2" />
            ) : (
              ""
            )}
            <Appointments appointments={course.appointments} className="mt-2" />
          </CourseKind>
        ) : course.part ? (
          <ul class="tk-list-disc tk-ml-6 tk-pn-6 tk-mt-4">
            <PartItem
              key={course.part.id}
              part={course.part}
              liClass="mb-6 last-child-mb-0"
              className="font-semibold text-lg pb-4"
            >
              <div class="mt-2">
                <PartNote part={course.part} className="mt-2" />
                <Appointments
                  appointments={course.appointments}
                  className="mt-2"
                />
              </div>
            </PartItem>
          </ul>
        ) : (
          <Appointments appointments={course.appointments} className="mt-2" />
        )}
      </div>
      {/* <YamlData data={course.parts} /> */}
    </div>
  )
}

export const Course: React.FC<Course> = ({ course }) => (
  <Foldable
    heading={<ModuleHeading module={course.module} />}
    details={<CourseDetails course={course} />}
  />
)

interface CoursesContainer {
  courses: CoursesYaml[]
  activeStpo: StposYaml
  //heading: string
  category: string
  className: string
  semester: SemestersYaml
}

export const CoursesContainer: React.FC<CoursesContainer> = ({
  courses,
  activeStpo,
  //heading,
  category,
  className,
  semester,
}) => (
  <Container
    heading={<CategoryHeading activeStpo={activeStpo} category={category} />}
    className={className}
    collection={courses}
    filter={c =>
      c.semester.id === semester.id && category === c.module.category
    }
    order={(a, b) =>
      a.module.schedule[0].semester - b.module.schedule[0].semester
    }
    item={(course: CoursesYaml) => (
      <li
        key={course.id}
        class={`${
          course?.module.schedule.some(s => s.stpo.id === activeStpo.id)
            ? ""
            : "hidden"
        } px-4 py-4 mb-4 last-child-mb-0 hover:bg-gray-100 bg-white shadow-lg transition-colors duration-500`}
      >
        <Course course={course} />
      </li>
    )}
  />
)

export const coursesDataQuery = graphql`
  fragment CoursesData on Query {
    allCoursesYaml {
      nodes {
        ...Courses
      }
    }
    allStposYaml {
      nodes {
        ...StPOs
      }
    }
    allSemestersYaml {
      nodes {
        ...Semesters
      }
    }
  }
`

interface Courses {
  courses: CoursesYaml[]
  semesters: SemestersYaml[]
  // categories: String[]
  stpos: StposYaml[]
  className?: string
  itemClassName?: string
  heading?: (activeStpo: StposYaml) => JSX.Element
}

export const Courses: React.FC<Courses> = ({
  courses,
  semesters,
  // categories,
  stpos,
  className = "",
  itemClassName = "",
  heading = activeStpo => (
    <div class="mb-6">
      <h2 class="text-2xl sm:text-3xl inline-block mr-4">
        Lehrveranstaltungen
      </h2>
      <p class="inline-block text-umr-grey-500">
        gemäß der {activeStpo?.title.short}
      </p>
    </div>
  ),
}) => {
  const [activeStpo, setActiveStpo] = useState(stpos[stpos.length - 1])
  return (
    <div class={className}>
      <StpoSelector
        stpos={stpos}
        activeStpo={activeStpo}
        setActiveStpo={setActiveStpo}
      />
      {heading(activeStpo)}
      <div class="pl-4 bg-umr-grey-100">
        <ol class="flex flex-wrap">
          {semesters
            .filter(s => isSameOrAfterSemester(s, activeStpo.since))
            .map(semester => (
              <li key={semester.id} class="mb-16 last-child-mb-0 w-full">
                <SemesterHeading semester={semester} />
                <ol class="flex flex-wrap">
                  {categories.map(category => (
                    <li
                      class={`w-full md:w-1/2 mb-0 p-4 pl-0 last-child-mb-0 md:second-last-child-mb-0 ${itemClassName}`}
                      key={semester.id + category}
                    >
                      <CoursesContainer
                        semester={semester}
                        courses={courses}
                        activeStpo={activeStpo}
                        category={category}
                        className="md:w-full"
                      />
                    </li>
                  ))}
                </ol>
              </li>
            ))}
        </ol>
      </div>
    </div>
  )
}

interface FilteredCourses {
  semestersFilter?: (s: SemestersYaml) => boolean
  className?: string
  itemClassName?: string
  heading?: (activeStpo: StposYaml) => JSX.Element
  order?
}

export const FilteredCourses: React.FC<FilteredCourses> = ({
  semestersFilter = s => true,
  className = "",
  itemClassName = "",
  heading,
  order = semestersOrder,
}) => {
  const data: FilteredCoursesQuery = useStaticQuery(graphql`
    query FilteredCourses {
      ...CoursesData
    }
  `)
  const stpos = data.allStposYaml.nodes
  const semesters = data.allSemestersYaml.nodes
    .filter(semestersFilter)
    .sort(order)
  const courses = data.allCoursesYaml.nodes
  return (
    <Courses
      stpos={stpos}
      semesters={semesters}
      courses={courses}
      categories={categories}
      className={className}
      itemClassName={itemClassName}
      heading={heading}
    />
  )
}
